import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'primary';
  /** Place le focus sur le bouton de confirmation (Entrée confirme, Échap annule). */
  autoFocusConfirm?: boolean;
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger',
  autoFocusConfirm = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const resolvedConfirmText = confirmText || t('confirm');
  const resolvedCancelText = cancelText || t('cancel');
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onKeyDown={autoFocusConfirm ? (e) => { if (e.key === 'Escape') onClose(); } : undefined}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-6">{message}</p>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose}>
              {resolvedCancelText}
            </Button>
            <Button
              variant={variant === 'danger' ? 'danger' : variant === 'warning' ? 'primary' : 'primary'}
              onClick={handleConfirm}
              autoFocus={autoFocusConfirm}
            >
              {resolvedConfirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface RecurringDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteAllFuture: () => void;
  itemType: string; // "revenu", "dépense", etc.
}

interface RecurringSaveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSingle: () => void;
  onSaveAllFuture: () => void;
}

export function RecurringSaveDialog({
  isOpen,
  onClose,
  onSaveSingle,
  onSaveAllFuture,
}: RecurringSaveDialogProps) {
  const { t } = useTranslation('common');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('confirmDialog.editRecurringTitle')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('confirmDialog.editRecurringMessage')}
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              onClick={() => {
                onSaveSingle();
                onClose();
              }}
              className="w-full justify-center"
            >
              {t('confirmDialog.editRecurringSingleOnly')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onSaveAllFuture();
                onClose();
              }}
              className="w-full justify-center"
            >
              {t('confirmDialog.editRecurringAllFuture')}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full justify-center text-gray-500"
            >
              {t('cancel')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RecurringDeleteDialog({
  isOpen,
  onClose,
  onDeleteAllFuture,
  itemType,
}: RecurringDeleteDialogProps) {
  const { t } = useTranslation('common');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('confirmDialog.deleteRecurringTitle', { itemType })}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('confirmDialog.deleteRecurringMessage')}
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              variant="danger"
              onClick={() => {
                onDeleteAllFuture();
                onClose();
              }}
              className="w-full justify-center"
            >
              {t('confirmDialog.deleteRecurringConfirm')}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full justify-center text-gray-500"
            >
              {t('cancel')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
